import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  InputGroup,
  FormControl,
  Button,
  Table,
  Form
} from 'react-bootstrap';
import Flex from 'components/common/Flex';
import ActionButton from 'components/common/ActionButton';
import InspectionGroupModal from 'components/pages/haccp/inspStandardInfo/ingredientsGroup/modal/InspectionGroupModal';

import { ACTION_TYPE, USE_YN_SELECT } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
import { useConfirmModalState } from 'global/confirmModalState';

// api
import {
  inspGroupCreate,
  inspGroupDetail,
  inspGroupUpdate
} from 'api/haccp/inspStandardInfo/ingredientsGroupApi';
import { commonCode, category } from 'api/haccp/commonApi';

/**
 * @description 시료기준관리(성분별 템플릿)/(기존 검사성분그룹) 상세페이지
 */

const Detail = () => {
  /* router info*/
  const history = useHistory();
  const { state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  const [modalShow, setModalShow] = useState(false);
  const [action, setAction] = useState('');
  const [actionType, setActionType] = useState('');
  const [validated, setValidated] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState(null);
  const [ingredientOptions, setIngredientOptions] = useState(null);

  // 시료기준관리(성분별 템플릿) 정보
  const [groupInfo, setGroupInfo] = useState({
    ctgryGrpCd: null, // 검사분류 코드
    inspIrdntGrpNm: null, // 템플릿명
    description: null, // 비고
    useYn: 'Y', // 사융유무
    isDisabled: false
  });

  // 검사성분 한계기준정보
  const [groupInfoList, setGroupInfoList] = useState([]);
  // 검사성분 한계기준정보 원본데이터
  const [groupInfoListOrigin, setGroupInfoListOrigin] = useState([]);

  /* event */
  const handleSubmit = event => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    if (groupInfoList.length === 0) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '검사성분 한계기준정보를 등록해주세요.',
        isConfirmBtn: true
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `시료기준관리(성분별 템플릿) 정보를 ${action} 하시겠습니까?`,
      isCancelBtn: true,
      isConfirmBtn: true,
      onConfirm: () => {
        onSubmit();
      }
    });
  };

  const onSubmit = async () => {
    const { ctgryGrpCd, inspIrdntGrpNm, description, useYn } = groupInfo || {};

    let requestBody = {};
    let response = {};

    if (actionType === 'create') {
      // 등록
      requestBody = {
        ctgryGrpCd: ctgryGrpCd,
        inspIrdntGrpNm: inspIrdntGrpNm,
        description: description,
        useYn: useYn,
        inspIrdntGrpLimitList: groupInfoList?.map(e => ({
          inspTypeIrdntMapSeq: e.seq,
          inspLimitMthCd: e.inspLimitMthCd,
          inspLimitMin: e.inspLimitMin,
          inspLimitMax: e.inspLimitMax,
          ccpYn: e.ccpYn,
          useYn: e.useYn
        }))
      };

      response = await inspGroupCreate(requestBody);
    } else {
      // 수정
      requestBody = {
        ctgryGrpCd: ctgryGrpCd,
        inspIrdntGrpNm: inspIrdntGrpNm,
        description: description,
        useYn: useYn,
        inspIrdntGrpLimitList: groupInfoList?.map(e => ({
          seq: e.histType === 'C' ? null : e.seq,
          inspTypeIrdntMapSeq: e.inspTypeIrdntMapSeq,
          inspIrdntGrpSeq: e.inspIrdntGrpSeq,
          inspLimitMthCd: e.inspLimitMthCd,
          inspLimitMin: e.inspLimitMin,
          inspLimitMax: e.inspLimitMax,
          ccpYn: e.ccpYn,
          useYn: e.useYn,
          histType: e.histType,
          delYn: 'N'
        }))
      };

      response = await inspGroupUpdate(seq, requestBody);
    }

    const { code, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: `시료기준관리(성분별 템플릿) 정보 정상 ${action} 되었습니다.`,
      isConfirmBtn: true
    });

    // 등록일 경우만 조회조건 초기화 및 sort조건 변경
    if (actionType === 'create') {
      history.push({
        pathname: '/haccp/inspectionGroup',
        state: {
          ...state,
          ctgryGrpCd: '',
          useYn: '',
          inspIrdntGrpNm: '',
          page: 0,
          sort: 'seq,desc'
        }
      });
      return;
    }

    onList();
  };

  // modal Open
  const onModalOpen = () => {
    if (groupInfo.ctgryGrpCd === null) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: '분류를 먼저 선택해주세요.',
        isConfirmBtn: true
      });

      return;
    }

    setModalShow(true);
  };

  // modal data 선택
  const modalConfirm = value => {
    const array = [...value, ...groupInfoList?.filter(e => e.isCheck === true)];
    const duplicateArray = array?.reduce(function (acc, current) {
      if (
        acc.findIndex(({ mappingSeq }) => mappingSeq === current.mappingSeq) ===
        -1
      ) {
        acc.push(current);
      }
      return acc;
    }, []);

    setGroupInfoList(
      duplicateArray?.map((e, index) => {
        e.no = index + 1;
        // 신규등록된 검사성분목록
        e.histType = 'C';
        e.inspTypeIrdntMapSeq = e.seq;
        e.inspIrdntGrpSeq = Number(seq);
        e.inspLimitMthCd = null;
        e.ccpYn = 'N';
        e.useYn = 'Y';
        groupInfoListOrigin?.forEach(data => {
          // 기존 검사성분목록
          if (e.mappingSeq === data.inspTypeIrdntMapSeq) {
            e.histType = 'U';
            e.seq = data.seq;
            e.inspTypeIrdntMapSeq = data.inspTypeIrdntMapSeq;
            e.inspIrdntGrpSeq = data.inspIrdntGrpSeq;
            e.inspLimitMthCd = data.inspLimitMthCd;
            e.inspLimitMin = data.inspLimitMin;
            e.inspLimitMax = data.inspLimitMax;
            e.ccpYn = data.ccpYn;
            e.useYn = data.useYn;
          }
        });
        return e;
      })
    );

    setModalShow(false);
  };

  // 검사성분 한계기준정보 삭제
  const groupInfoDelete = index => {
    setConfirmModal({
      isOpen: true,
      title: '확인',
      message: '검사성분 한계기준정보를 삭제하시겠습니까?',
      isConfirmBtn: true,
      isCancelBtn: true,
      onConfirm: () => {
        const newList = [...groupInfoList];
        newList.splice(index, 1);
        setGroupInfoList(newList);
      }
    });
  };

  // 시료기준관리(성분별 템플릿) 정보 -> 분류 선택
  const onCategory = e => {
    const { value } = e.target || {};

    if (value !== null && groupInfoList.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message:
          '분류변경 시 검사성분 한계기준정보가 초기화됩니다. \n 변경하시겠습니까?',
        isConfirmBtn: true,
        isCancelBtn: true,
        onConfirm: () => {
          setGroupInfoList([]);
          setGroupInfo({
            ...groupInfo,
            ctgryGrpCd: value
          });
        }
      });
      return;
    }

    setGroupInfo({
      ...groupInfo,
      ctgryGrpCd: value
    });
  };

  // 목록페이지 이동
  const onList = () => {
    history.push({
      pathname: '/haccp/inspectionGroup',
      state: state
    });
  };

  // 검사성분 한계기준정보 데이터 update
  const updateFieldChanged = (e, index) => {
    const { name, value } = e.target || {};

    const newList = [...groupInfoList];

    // 검사종류 선택시 max, min Data 초기화
    if (name === 'inspLimitMthCd') {
      newList[index] = {
        ...newList[index],
        [name]: value,
        inspLimitMin: null,
        inspLimitMax: null
      };
      setGroupInfoList(newList);
      return;
    }

    newList[index] = { ...newList[index], [name]: value };
    setGroupInfoList(newList);
  };

  /* method */
  // 시료기준관리(성분별 템플릿) 상세 API
  const onDetail = async () => {
    const response = await inspGroupDetail(seq);

    const { code, data, message } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: message,
        isConfirmBtn: true
      });
      return;
    }

    const {
      ctgryGrpCd,
      inspIrdntGrpNm,
      useYn,
      description,
      inspIrdntGrpLimitList
    } = data || {};

    // 시료기준관리(성분별 템플릿) 정보
    setGroupInfo({
      ...groupInfo,
      ctgryGrpCd: ctgryGrpCd, // 검사분류코드
      inspIrdntGrpNm: inspIrdntGrpNm, // 템플릿명
      useYn: useYn, // 사용유무
      description: description, // 비고
      isDisabled: true
    });

    // 검사성분 한계기준정보
    setGroupInfoList(
      inspIrdntGrpLimitList?.map((e, index) => ({
        ...e,
        no: index + 1,
        histType: 'U',
        mappingSeq: e.inspTypeIrdntMapSeq,
        isCheck: true
      })) ?? []
    );

    setGroupInfoListOrigin(inspIrdntGrpLimitList);
  };

  // 분류목록 API
  const categoryList = async () => {
    const response = await category();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setCategoryOptions(list);
  };

  // 검사방법 AP API
  const inspTypeList = async () => {
    const params = {
      codeGroup: 'sfsh_limit_mth',
      useYn: 'Y',
      sort: 'orders,asc'
    };
    const response = await commonCode(params);

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setIngredientOptions(list);
  };

  /* watch */
  useEffect(() => {
    // 검사분류 API호출
    categoryList();
    // 검사방법 API
    inspTypeList();
  }, []);

  useEffect(() => {
    switch (actionType) {
      case ACTION_TYPE.CREATE: {
        setAction('등록');
        break;
      }
      case ACTION_TYPE.UPDATE: {
        setAction('수정');
        onDetail();
        break;
      }
    }
  }, [actionType]);

  useLayoutEffect(() => {
    if (seq === undefined) {
      setActionType('create');
      return;
    }

    setActionType('update');
  }, []);

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="align-items-center g-2">
              <Col>
                <h5 className="mb-0">시료기준관리(성분별 템플릿) 정보</h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="bg-light">
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*분류</Form.Label>
                  <InputGroup>
                    <Form.Select
                      required
                      size="sm"
                      onChange={e => onCategory(e)}
                      value={groupInfo.ctgryGrpCd || ''}
                      disabled={groupInfo.isDisabled}
                    >
                      <option hidden value="">
                        분류 선택해주세요.
                      </option>
                      {categoryOptions?.map(option => (
                        <option key={option.ctgryCd} value={option.ctgryCd}>
                          {option.ctgryNmKr}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*템플릿 명</Form.Label>
                  <InputGroup>
                    <FormControl
                      required
                      type="text"
                      placeholder="50자 이내로 입력해주세요."
                      size="sm"
                      maxLength="50"
                      value={groupInfo.inspIrdntGrpNm || ''}
                      onChange={e =>
                        setGroupInfo({
                          ...groupInfo,
                          inspIrdntGrpNm: e.target.value
                        })
                      }
                      disabled={groupInfo.isDisabled}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>비고</Form.Label>
                  <InputGroup>
                    <FormControl
                      type="text"
                      placeholder="20자 이내로 입력해주세요."
                      size="sm"
                      maxLength="20"
                      value={groupInfo.description || ''}
                      onChange={e =>
                        setGroupInfo({
                          ...groupInfo,
                          description: e.target.value
                        })
                      }
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>*사용유무</Form.Label>
                  <Flex>
                    <Form.Check
                      type="radio"
                      id="use"
                      label="사용"
                      name="useYn"
                      value="Y"
                      checked={groupInfo.useYn === 'Y'}
                      onChange={e =>
                        setGroupInfo({
                          ...groupInfo,
                          useYn: e.target.value
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      id="unuse"
                      label="미사용"
                      className="ms-3"
                      name="useYn"
                      value="N"
                      checked={groupInfo.useYn === 'N'}
                      onChange={e =>
                        setGroupInfo({
                          ...groupInfo,
                          useYn: e.target.value
                        })
                      }
                    />
                  </Flex>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Row className="align-items-center g-2">
              <Col>
                <h5 className="mb-0">검사성분 한계기준정보</h5>
              </Col>
              <Col xs={'auto'}>
                <Button
                  size="sm"
                  className="ms-2"
                  onClick={() => onModalOpen()}
                >
                  검사종류 선택
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="pt-0">
            <Table responsive bordered className="fs--1 text-center mb-0">
              <thead className="bg-200 text-700 align-middle border-300">
                <tr>
                  <th>No.</th>
                  <th>검사종류</th>
                  <th>의뢰기관</th>
                  <th>검사성분 코드</th>
                  <th>검사결과서 코드</th>
                  <th>검사성분명</th>
                  <th>검사성분유형</th>
                  <th>주기</th>
                  <th>*검사 방법</th>
                  <th>*한계기준값</th>
                  <th>*CCP관리</th>
                  <th>*사용유무</th>
                  <th>*관리</th>
                </tr>
              </thead>
              <tbody>
                {groupInfoList?.map(
                  (
                    {
                      no,
                      inspLabNm,
                      inspIrdntCd,
                      labInspIrdntCd,
                      inspIrdntNm,
                      inspTypeNm,
                      inspIrdntKindNm,
                      cycleNm,
                      inspLimitMthCd,
                      inspLimitMin,
                      inspLimitMax,
                      ccpYn,
                      useYn
                    },
                    index
                  ) => (
                    <tr key={index}>
                      <td>{no}</td>
                      <td>{inspTypeNm}</td>
                      <td>{inspLabNm}</td>
                      <td>{inspIrdntCd}</td>
                      <td>{labInspIrdntCd}</td>
                      <td>{inspIrdntNm}</td>
                      <td>{inspIrdntKindNm || '-'}</td>
                      <td>{cycleNm}</td>
                      <td>
                        <Form.Select
                          required
                          size="sm"
                          name="inspLimitMthCd"
                          value={inspLimitMthCd || ''}
                          onChange={e => updateFieldChanged(e, index)}
                        >
                          <option hidden value={''}>
                            검사방법선택
                          </option>
                          {ingredientOptions?.map(option => (
                            <option key={option.codeCd} value={option.codeCd}>
                              {option.codeNmKr}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      {/* 한계기준값 선택에 따른 구분 */}
                      <td style={{ width: '15%' }}>
                        {/* MIN / MAX 선택 시 */}
                        {inspLimitMthCd === 'mth_mm' && (
                          <Flex alignItems="center">
                            <Form.Control
                              required={useYn === 'Y'}
                              name="inspLimitMin"
                              type="number"
                              placeholder="하한값"
                              size="sm"
                              value={inspLimitMin ?? ''}
                              onChange={e => updateFieldChanged(e, index)}
                            />
                            <span className="mx-3">/</span>
                            <Form.Control
                              required={useYn === 'Y'}
                              name="inspLimitMax"
                              type="number"
                              placeholder="상한값"
                              size="sm"
                              value={inspLimitMax ?? ''}
                              onChange={e => updateFieldChanged(e, index)}
                            />
                          </Flex>
                        )}
                        {/* MIN 선택 시 */}
                        {inspLimitMthCd === 'mth_min' && (
                          <Flex alignItems="center">
                            <Form.Control
                              required={useYn === 'Y'}
                              type="number"
                              placeholder="값을 입력해주세요."
                              size="sm"
                              name="inspLimitMin"
                              value={inspLimitMin ?? ''}
                              onChange={e => updateFieldChanged(e, index)}
                            />
                            {/* MIN */}
                            <span className="ms-3 flex-grow-1 flex-shrink-0">
                              이상
                            </span>
                          </Flex>
                        )}
                        {/* MAX 선택 시 */}
                        {inspLimitMthCd === 'mth_max' && (
                          <Flex alignItems="center">
                            <FormControl
                              required={useYn === 'Y'}
                              type="number"
                              placeholder="값을 입력해주세요."
                              size="sm"
                              name="inspLimitMax"
                              value={inspLimitMax ?? ''}
                              onChange={e => updateFieldChanged(e, index)}
                            />
                            <span className="ms-3 flex-grow-1 flex-shrink-0">
                              이하
                            </span>
                          </Flex>
                        )}
                        {/* 검출여부 선택 시 */}
                        {inspLimitMthCd === 'mth_dtc' && (
                          <Form.Control
                            required
                            type="text"
                            size="sm"
                            value={'불검출'}
                            disabled={true}
                          />
                        )}
                        {/* 검출여부 미선택 */}
                        {inspLimitMthCd === null && <span>-</span>}
                      </td>
                      {/* CCP관리여부 Select Box */}
                      <td style={{ width: '10%' }}>
                        <Form.Select
                          required
                          size="sm"
                          name="ccpYn"
                          value={ccpYn}
                          onChange={e => updateFieldChanged(e, index)}
                        >
                          {USE_YN_SELECT?.map(option => (
                            <option key={option.key} value={option.key}>
                              {option.value}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      {/* 사용여부 Select Box */}
                      <td style={{ width: '10%' }}>
                        <Form.Select
                          size="sm"
                          name="useYn"
                          value={useYn}
                          onChange={e => updateFieldChanged(e, index)}
                        >
                          {USE_YN_SELECT?.map(option => (
                            <option key={option.key} value={option.key}>
                              {option.value}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td style={{ width: '5%' }}>
                        <ActionButton
                          icon="trash-alt"
                          title="삭제"
                          variant="action"
                          className="p-0 lh-1 text-danger"
                          onClick={() => groupInfoDelete(index)}
                        />
                      </td>
                    </tr>
                  )
                )}
                {groupInfoList?.length < 1 && (
                  <tr>
                    <td colSpan={13} className="w-auto">
                      검사성분 한계기준정보 정보가 존재하지 않습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
          <Card.Footer className="d-flex align-items-center">
            <Button
              size="sm"
              variant="secondary"
              className="ms-auto"
              onClick={() => onList()}
            >
              취소
            </Button>
            <Button size="sm" className="ms-2" type="submit">
              {action}
            </Button>
          </Card.Footer>
        </Card>
      </Form>
      {modalShow === true && (
        <InspectionGroupModal
          modalShow={modalShow}
          setModalShow={setModalShow}
          modalConfirm={modalConfirm}
          itemList={groupInfoList}
          setItemList={setGroupInfoList}
          itemInfo={groupInfo}
        ></InspectionGroupModal>
      )}
    </>
  );
};

export default Detail;
